import React, { useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import { GenericAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'

import {
  trackAccountReopenWelcomeBackAccessFormsButtonTappedPayload,
  trackAccountReopenWelcomeBackReopenAccountButtonTappedPayload,
  trackAccountReopenWelcomeBackScreenViewedPayload,
  trackAccountReopenWelcomeBackUploadDocumentsButtonTappedPayload,
} from 'generated/trackingEvents'
import { reopenFlowTypeInReopenFunnel } from 'utils/analytics/reopenFlowAnalytics'
import {
  CLOSED_ACCOUNT_REOPEN_ACCOUNT_BUTTON,
  CLOSED_ACCOUNT_STATEMENTS_AND_TAXES_BUTTON,
} from 'utils/testing/testIDs'

import Card from './CardMessage'
import { GetStep } from './ClosedAccount'
import {
  ButtonContainer,
  StyledButton,
  UploadDocuments,
} from './styled-components'

type Props = {
  onPress: () => void
  setStep: (index: number) => void
  getStep: GetStep
  analytics: GenericAnalyticsClient
  isLongReopen?: boolean
}

const UPLOAD_DOCUMENTS = 'Need to upload documents? '
export const SHOW_STATEMENTS = 'Statements and tax forms'

export const WelcomeBack = (props: Props) => {
  const history = useHistory()
  const { getRemoteString } = useRemoteStrings()
  const reopenFlowType = reopenFlowTypeInReopenFunnel(props.isLongReopen)
  const onStatementsAndTaxesPress = () => {
    props.analytics.track(
      ...trackAccountReopenWelcomeBackAccessFormsButtonTappedPayload({
        funnel: 'reopen',
        type: reopenFlowType,
        ctaTitle: SHOW_STATEMENTS,
      }),
    )
    history.push('/documents/core/statements')
  }

  const onLinktoDocumentUploaderPress = () => {
    props.analytics.track(
      ...trackAccountReopenWelcomeBackUploadDocumentsButtonTappedPayload({
        funnel: 'reopen',
        type: reopenFlowType,
      }),
    )
  }

  const DocumentUploadLink = () => (
    <UploadDocuments>
      {UPLOAD_DOCUMENTS}
      <Link to="/document-uploader" onClick={onLinktoDocumentUploaderPress}>
        Click here
      </Link>
    </UploadDocuments>
  )

  const REOPEN_ACCOUNT = getRemoteString({
    key: 'closed_account.welcome_back.cta_title',
  })

  useEffect(() => {
    props.analytics.track(
      ...trackAccountReopenWelcomeBackScreenViewedPayload({
        funnel: 'reopen',
        type: reopenFlowType,
      }),
    )
  }, [])

  const onReopenMyAccountPress = () => {
    props.analytics.track(
      ...trackAccountReopenWelcomeBackReopenAccountButtonTappedPayload({
        funnel: 'reopen',
        type: reopenFlowType,
      }),
    )
    props.setStep(props.getStep('Confirmation'))
  }

  return (
    <Card
      title={getRemoteString({ key: 'closed_account.welcome_back.title' })}
      icon={<AcornsIcon icon={AcornsIcon.Icon.AcornsLogo} width="32px" />}
      iconAlt="Acorns logo"
      text={getRemoteString({ key: 'closed_account.welcome_back.text' })}
      additionalText={<DocumentUploadLink />}
    >
      <ButtonContainer>
        <StyledButton
          onPress={onReopenMyAccountPress}
          margin="0 auto"
          data-testid={CLOSED_ACCOUNT_REOPEN_ACCOUNT_BUTTON}
        >
          {REOPEN_ACCOUNT}
        </StyledButton>
        <StyledButton
          data-testid={CLOSED_ACCOUNT_STATEMENTS_AND_TAXES_BUTTON}
          onPress={onStatementsAndTaxesPress}
          type="flat"
          secondary
          margin="10px auto 0"
        >
          {SHOW_STATEMENTS}
        </StyledButton>
      </ButtonContainer>
    </Card>
  )
}
